import { useQuery, useQueries, useMutation } from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"
import { getPastDate } from "util/formatters"

/** ------------------- **/
/*  Companies / Competitors
/** ------------------- **/

export function useCompanies(params: {
  startDate?: string
  endDate?: string
  search?: string | undefined
  productSearch?: string | undefined
  vertical?: string | undefined
  revenue?: string | undefined
  pageSize?: string | number
  pageOffset?: string | number
}) {
  const { search, productSearch, vertical, revenue, pageSize, pageOffset } =
    params
  const splitRevenue = revenue?.split("-")
  const revenue_min = splitRevenue?.[0] ? splitRevenue[0] : undefined
  const revenue_max = splitRevenue?.[1] ? splitRevenue[1] : undefined
  const paramsString = qs.stringify({
    search: search ? search : "",
    product_term: productSearch ? productSearch : "",
    vertical: vertical ? vertical : undefined,
    revenue_min,
    revenue_max,
    page_size: pageSize ? pageSize : 25,
    page_number: pageOffset ? pageOffset : 0,
  })
  const cacheKey = ["companies", params]
  const query = () => apiRequest(`companies/list?${paramsString}`)
  return useQuery(cacheKey, query)
}

export function useCompaniesExport() {
  return useMutation(
    ({
      search,
      productSearch,
      vertical,
      revenue,
      pageOffset,
    }: {
      search: string | undefined
      productSearch: string | undefined
      vertical: string | undefined
      revenue: string | undefined
      pageOffset: number | undefined
    }) => {
      const splitRevenue = revenue?.split("-")
      const revenue_min = splitRevenue?.[0] ? splitRevenue[0] : undefined
      const revenue_max = splitRevenue?.[1] ? splitRevenue[1] : undefined
      const paramsString = qs.stringify({
        search: search ? search : "",
        product_term: productSearch ? productSearch : "",
        vertical: vertical ? vertical : undefined,
        revenue_min,
        revenue_max,
        page_size: 10000,
        page_number: pageOffset ? pageOffset : 0,
      })
      return apiRequest(`companies/list?${paramsString}`)
    },
    {
      onSuccess: () => {
        toast.success("Your download has started!")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue generating this report")
      },
    }
  )
}

export function useCompanyDetails({ id }: { id: string | undefined }) {
  const cacheKey = ["companyDetails", id]
  const query = () => apiRequest(`companies/${id}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompanySales({
  id,
  startDate,
  endDate,
  searchTerm = undefined,
  selectedProductTypes = "",
  selectedColors = "",
  selectedGenders = "",
}: {
  id: string
  startDate?: string
  endDate?: string
  searchTerm?: string
  selectedProductTypes?: string
  selectedColors?: string
  selectedGenders?: string
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
    search: searchTerm,
    product_types: selectedProductTypes,
    colors: selectedColors,
    genders: selectedGenders,
  })
  const cacheKey = [
    "companySales",
    id,
    startDate,
    endDate,
    searchTerm,
    selectedProductTypes,
    selectedColors,
    selectedGenders,
  ]
  const query = () => apiRequest(`companies/${id}/sales?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useMultipleCompanyDetails({
  companyIds,
}: {
  companyIds: string[]
}) {
  const useQueriesArray =
    companyIds?.map(companyId => ({
      queryKey: ["companyDetails", companyId],
      queryFn: () => apiRequest(`companies/${companyId}`),
    })) ?? []
  return useQueries({ queries: useQueriesArray })
}

export function useMultipleCompanySales({
  companies,
  startDate,
  endDate,
}: {
  companies: {
    id: string
  }[]
  startDate?: string
  endDate?: string
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const useQueriesArray = companies?.map(company => ({
    queryKey: ["companySales", company.id, startDate, endDate],
    queryFn: () => apiRequest(`companies/${company.id}/sales?${paramsString}`),
  }))
  return useQueries({ queries: useQueriesArray })
}

export function useCompanyTopProducts({
  id,
  limit = 2,
  startDate,
  endDate,
  skipFilters = false,
  searchTerm = undefined,
  selectedProductTypes = "",
  selectedColors = "",
  selectedGenders = "",
  selectedSizes = "",
}: {
  id: string
  limit?: number
  startDate?: string
  endDate?: string
  skipFilters?: boolean
  searchTerm?: string
  selectedProductTypes?: string
  selectedColors?: string
  selectedGenders?: string
  selectedSizes?: string
}) {
  const paramsString = qs.stringify({
    limit,
    start_date: startDate,
    end_date: endDate,
    skipFilters,
    search: searchTerm,
    product_types: selectedProductTypes,
    colors: selectedColors,
    genders: selectedGenders,
    sizes: selectedSizes,
  })
  const cacheKey = [
    "companyTopProducts",
    id,
    limit,
    startDate,
    endDate,
    skipFilters,
    searchTerm,
    selectedProductTypes,
    selectedColors,
    selectedGenders,
    selectedSizes,
  ]
  const query = () =>
    apiRequest(`companies/${id}/top_products_v2?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompanyTrendingProducts({ id }: { id: string }) {
  const cacheKey = ["companyTrendingProducts", id]
  const query = () => apiRequest(`companies/${id}/trending_products`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompanyProductGroups({ id }: { id: string }) {
  const cacheKey = ["companyProductGroups", id]
  const query = () => apiRequest(`companies/${id}/product_groups`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompanyMarketingEvents(
  {
    id,
    startDate,
    endDate,
    marketDataType = "social",
  }: {
    id: string | undefined
    startDate?: string
    endDate?: string
    marketDataType?: string
  },
  enabled = true
) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
    market_data_type: marketDataType,
  })
  const cacheKey = [
    "companyMarketingEvents",
    id,
    startDate,
    endDate,
    marketDataType,
  ]
  const query = () => apiRequest(`companies/${id}/market_data?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id && !!enabled })
}

export function useCompanyMarketingMeta({
  id,
  marketDataType = "details,social,ads,traffic",
}: {
  id: string
  marketDataType?: string
}) {
  const paramsString = qs.stringify({
    request_type: marketDataType,
  })
  const cacheKey = ["companyMarketingMeta", id, marketDataType]
  const query = () =>
    apiRequest(`companies/${id}/external_meta_data?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompetitorSnapshot(companyIdsArray: string[]) {
  const companyIdsString = companyIdsArray.join(",")

  const paramsString = qs.stringify({
    id: companyIdsString,
    startDate: getPastDate(7, "day"),
    endDate: getPastDate(1, "day"),
  })
  const cacheKey = ["competitorSnapshot", companyIdsString]
  const query = () => apiRequest(`companies/snapshot?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!companyIdsArray.length })
}

export function useCompaniesOverview({
  startDate,
  endDate,
}: {
  startDate?: string
  endDate?: string
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = ["companiesOverview", startDate, endDate]
  const query = () => apiRequest(`companies/overview?${paramsString}`)
  return useQuery(cacheKey, query)
}

export function useCompaniesTopProducts({
  ids,
  limit,
  startDate,
  endDate,
  searchTerm,
  selectedProductTypes,
  selectedColors,
  selectedGenders,
  selectedSizes,
}: {
  ids?: string[]
  limit?: number
  startDate?: string
  endDate?: string
  skipFilters?: boolean
  searchTerm?: string
  selectedProductTypes?: string
  selectedColors?: string
  selectedGenders?: string
  selectedSizes?: string
}) {
  // sort the ids so that the queryKey is deterministic
  const joinedIds = ids?.sort()?.join(",")
  const paramsString = qs.stringify({
    ids: joinedIds || "",
    limit,
    start_date: startDate,
    end_date: endDate,
    search: searchTerm,
    product_types: selectedProductTypes,
    colors: selectedColors,
    genders: selectedGenders,
    sizes: selectedSizes,
    competitor_overview: true,
  })
  const cacheKey = ["companiesTopProducts", paramsString]
  const query = () => apiRequest(`companies/top_products?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!ids?.length })
}
